import { AssignmentModel, AssignmentTypeEnum, CaseModel, CaseTypeEnum } from 'api/model';
import { isSameSwedishDay } from 'utils/date-helpers';

const PERSONAL_TRANSPORT_TYPES: ReadonlyArray<AssignmentTypeEnum> = [
  AssignmentTypeEnum.GoByTrain,
  AssignmentTypeEnum.GoByBus,
  AssignmentTypeEnum.GoByPlane,
  AssignmentTypeEnum.GoByBoat,
  AssignmentTypeEnum.GoByTaxi,
  AssignmentTypeEnum.GoByPublicTransport,
];

const SAMAKNING_REG_NR = 'SAMÅKNING';

const isPersonalTransport = (a: AssignmentModel): boolean =>
  PERSONAL_TRANSPORT_TYPES.includes(a.assignmentTypeID);

/**
 * Case som aldrig kan vara direktbok per policy:
 * - SAMÅKNING-regnr (intern carpool, aldrig kundfaktura).
 * - Annons / InternalDelivery (intern annonsleverans, aldrig kundfaktura).
 *
 * TL får ingen prompt och Direktbok-raden visas inte i case-vyn.
 * Mirrors AssignmentLogic.IsNeverDirectBooking.
 */
export const isNeverDirectBooking = (caseModel: CaseModel): boolean => {
  if (caseModel.caseTypeID === CaseTypeEnum.InternalDelivery) return true;
  return caseModel.registrationNumber?.trim().toUpperCase() === SAMAKNING_REG_NR;
};

/**
 * Direktbok-relevanta case-typer där TL kan flagga ärendet via UI:t. Styr
 * Direktbok-radens synlighet och modal-prompten vid save. Endast Purchase,
 * HomeDelivery och TradeIn — Sump (avvikande jobb), Annons och SAMÅKNING är
 * aldrig direktbok.
 *
 * Ride-importerade case sätts vid intake (RideCreateOrUpdateCase), så modalen
 * triggar inte för dem (caseNeedsTLDirectBookingAnswer ser att svaret redan finns).
 *
 * Mirrors AssignmentLogic.RequiresExplicitDirectBookingFlag.
 */
export const requiresExplicitDirectBookingFlag = (caseModel: CaseModel): boolean => {
  if (isNeverDirectBooking(caseModel)) return false;

  switch (caseModel.caseTypeID) {
    case CaseTypeEnum.Purchase:
    case CaseTypeEnum.HomeDelivery:
    case CaseTypeEnum.TradeIn:
    case CaseTypeEnum.Auction:
      return true;
    default:
      return false;
  }
};

/**
 * Within a case, returns the single assignment the same-day direktbok rule targets.
 * HomeDelivery uses the *last* non-personal-transport assignment (the actual delivery leg).
 * Other case types use the *first* non-personal-transport assignment.
 * Mirrors AssignmentLogic.GetSameDayDirektbokCandidateAssignment.
 */
export const getSameDayDirektbokCandidate = (
  caseModel: CaseModel
): AssignmentModel | undefined => {
  const nonPersonal = caseModel.assignmentList
    .filter((a) => !isPersonalTransport(a))
    .slice()
    .sort((a, b) => {
      const at = a.bookedTo?.getTime() ?? 0;
      const bt = b.bookedTo?.getTime() ?? 0;
      if (at !== bt) return at - bt;
      return a.assignmentID - b.assignmentID;
    });

  if (nonPersonal.length === 0) return undefined;

  return caseModel.caseTypeID === CaseTypeEnum.HomeDelivery
    ? nonPersonal[nonPersonal.length - 1]
    : nonPersonal[0];
};

/**
 * True when the case matches the same-day direktbok criteria — case type allows direktbok and the
 * candidate assignment's BookedTo falls on the same *svenska* calendar day as
 * Case.Created. Independent of isDirectBooking (unlike caseNeedsTLDirectBookingAnswer,
 * which also requires no answer yet).
 *
 * Mirrors AssignmentLogic.MatchesSameDayDirektbokCriteria at the case level.
 */
export const caseMatchesSameDayDirektbokCriteria = (
  caseModel: CaseModel
): boolean => {
  if (isNeverDirectBooking(caseModel)) return false;
  if (!requiresExplicitDirectBookingFlag(caseModel)) return false;

  const candidate = getSameDayDirektbokCandidate(caseModel);
  if (!candidate) return false;

  return isSameSwedishDay(candidate.bookedTo, caseModel.created);
};

/**
 * Returns true if the case matches the same-day direktbok criteria AND the TL has not yet
 * answered (CaseModel.isDirectBooking is null/undefined).
 */
export const caseNeedsTLDirectBookingAnswer = (
  caseModel: CaseModel
): boolean => {
  if (caseModel.isDirectBooking !== undefined && caseModel.isDirectBooking !== null) {
    return false;
  }
  return caseMatchesSameDayDirektbokCriteria(caseModel);
};

/**
 * True when the candidate ("main") assignment's BookedTo svenska calendar day differs
 * between the two cases — or when the candidate appears/disappears between them. Used by the edit-save guard to
 * decide whether the same-day-direktbok prompt should fire: we only prompt when the BookedTo
 * date actually changed, never when it stays the same.
 *
 * The candidate is the first non-personal-transport assignment (or the last, for HomeDelivery),
 * matching getSameDayDirektbokCandidate / AssignmentLogic.GetSameDayDirektbokCandidateAssignment.
 */
export const candidateBookedToDateChanged = (
  originalCase: CaseModel,
  newCase: CaseModel
): boolean => {
  const originalBookedTo = getSameDayDirektbokCandidate(originalCase)?.bookedTo;
  const newBookedTo = getSameDayDirektbokCandidate(newCase)?.bookedTo;

  if (!originalBookedTo && !newBookedTo) return false;
  if (!originalBookedTo || !newBookedTo) return true;
  return !isSameSwedishDay(originalBookedTo, newBookedTo);
};
